// src/services/FileManager/permissionManagement.service.ts
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import Notification from '../../shared/components/Notification/index';
import { NotificationTypes } from '../../enums/notificationTypes';
import axiosInstance from '../../interceptor/axiosInstance';
import { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';
import { QueryKeys } from '../../enums/queryKeys';
import { MutationKeys } from '../../enums/mutationKeys';
import { fileServiceMessages } from './file.service.constants';
import LayerInstance from '../../types/LayerInstance';

const PermissionManagerService = () => {
  const getAllUsers = (): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.GET_ALL_USERS],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(ApiRoutes.FILEMANAGER_USERS);
        return response.data;
      } catch (error) {
        Notification({
          message: fileServiceMessages.PERMISSIONS.GET_USERS_ERROR,
          description: error instanceof Error ? error.message : fileServiceMessages.FALLBACK_ERROR,
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
  });

  const getUser = (userId?: string): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.GET_USER, userId],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(ApiRoutes.FILEMANAGER_USER, {
          params: { userId },
        });
        return response.data;
      } catch (error) {
        Notification({
          message: fileServiceMessages.PERMISSIONS.GET_USER_ERROR,
          description: error instanceof Error ? error.message : fileServiceMessages.FALLBACK_ERROR,
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
    enabled: userId === undefined || !!userId,
  });

  const getAllGroups = (): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.GET_ALL_GROUPS],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(ApiRoutes.FILEMANAGER_GROUPS);
        return response.data;
      } catch (error) {
        Notification({
          message: fileServiceMessages.PERMISSIONS.GET_GROUPS_ERROR,
          description: error instanceof Error ? error.message : fileServiceMessages.FALLBACK_ERROR,
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
  });

  const getAllLayers = (): UseQueryOptions<LayerInstance[], Error> => ({
    queryKey: [QueryKeys.GET_ALL_LAYERS],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(ApiRoutes.FILEMANAGER_LAYERS);
        return response.data;
      } catch (error) {
        Notification({
          message: fileServiceMessages.PERMISSIONS.GET_LAYERS_ERROR,
          description: error instanceof Error ? error.message : fileServiceMessages.FALLBACK_ERROR,
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
  });

  const getVersionHistory = (versionId?: string): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.GET_VERSION_HISTORY, versionId],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(ApiRoutes.FILEMANAGER_VERSION_HISTORY, {
          params: { versionId },
        });
        return response.data;
      } catch (error) {
        Notification({
          message: fileServiceMessages.PERMISSIONS.GET_VERSION_HISTORY_ERROR,
          description: error instanceof Error ? error.message : fileServiceMessages.FALLBACK_ERROR,
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
    enabled: !!versionId,
  });

  const updatePermissions = (): UseMutationOptions<
    any,
    Error,
    { userValues: string[]; groupValues: string[]; resourceId: string; resourceType: string }
  > => ({
    mutationKey: [MutationKeys.UPDATE_PERMISSIONS],
    mutationFn: async ({ userValues, groupValues, resourceId, resourceType }) => {
      const response = await axiosInstance.post(ApiRoutes.FILEMANAGER_PERMISSIONS, {
        userPermissions: userValues,
        groupPermissions: groupValues,
        resourceId: resourceId,
        resourceType: resourceType,
      });
      return response.data;
    },
    onSuccess: () => {
      Notification({
        message: fileServiceMessages.PERMISSIONS.UPDATE_PERMISSIONS_SUCCESS,
        type: NotificationTypes.SUCCESS,
      });
    },
    onError: (error) => {
      Notification({
        message: fileServiceMessages.PERMISSIONS.UPDATE_PERMISSIONS_ERROR,
        description: error instanceof Error ? error.message : fileServiceMessages.FALLBACK_ERROR,
        type: NotificationTypes.ERROR,
      });
    },
  });

  const getPermissions = (resourceId?: string): UseQueryOptions<any, Error> => ({
    queryKey: [QueryKeys.GET_PERMISSIONS, resourceId],
    queryFn: async () => {
      try {
        const response = await axiosInstance.get(ApiRoutes.FILEMANAGER_PERMISSIONS, {
          params: { resourceId: resourceId },
        });
        return response.data;
      } catch (error) {
        Notification({
          message: fileServiceMessages.PERMISSIONS.GET_PERMISSIONS_ERROR,
          description: error instanceof Error ? error.message : fileServiceMessages.FALLBACK_ERROR,
          type: NotificationTypes.ERROR,
        });
        throw error;
      }
    },
    enabled: !!resourceId,
  });

  return {
    getAllUsers,
    getUser,
    getAllGroups,
    getAllLayers,
    updatePermissions,
    getPermissions,
    getVersionHistory,
  };
};

export default PermissionManagerService;
